'use client';

import { useForkRef } from '@mui/material/utils';
import { animate } from 'motion';
import { useReducedMotion } from 'motion/react';
import { cloneElement, forwardRef, isValidElement, useEffect, useRef, useState } from 'react';
const EASE_OUT = [0.23, 1, 0.32, 1];
const ENTER_OPTIONS = {
  duration: 0.2,
  ease: EASE_OUT
};
// Exit is quicker than enter: the choice is made, get out of the way.
const EXIT_OPTIONS = {
  duration: 0.15,
  ease: EASE_OUT
};
const HIDDEN_SCALE = 0.95;

// Keys consumed by the transition contract itself; everything else Popover puts on the
// transition element is meant for the paper and is forwarded (as in ConfirmDialogTransition).
const TRANSITION_ONLY_PROPS = new Set(['timeout', 'addEndListener', 'mountOnEnter', 'unmountOnExit', 'easing', 'enter', 'exit', 'nodeRef', 'TransitionComponent']);

/**
 * Popover/Menu transition: a short, uniform grow (0.95 → 1) with a strong ease-out, replacing MUI's
 * Grow (≈340ms for a tall menu, ease-in-out, squashed from 0.75 × 0.5625). Scales from the paper's
 * `transform-origin`, which Popover sets from `transformOrigin` while positioning. Fade only under
 * reduced motion. Use via `slots={{ transition: MenuGrowTransition }}`.
 */
export const MenuGrowTransition = forwardRef(function MenuGrowTransition({
  appear = true,
  children,
  in: inProp,
  onEnter,
  onEntering,
  onEntered,
  onExit,
  onExiting,
  onExited,
  style,
  ...rest
}, ref) {
  const nodeRef = useRef(null);
  const child = isValidElement(children) ? children : null;
  const handleRef = useForkRef(nodeRef, child?.props.ref, ref);
  const controlsRef = useRef(null);
  const isInitialMountRef = useRef(true);
  const [exited, setExited] = useState(!inProp);
  const reduceMotion = useReducedMotion();
  const hiddenScale = reduceMotion ? 1 : HIDDEN_SCALE;
  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    const isInitialMount = isInitialMountRef.current;
    isInitialMountRef.current = false;
    controlsRef.current?.stop();
    if (inProp) {
      setExited(false);
      if (isInitialMount && !appear) {
        node.style.opacity = '1';
        node.style.transform = 'none';
      } else {
        // Popover positions the paper (and sets its transform-origin) in onEntering, before we animate.
        onEnter?.(node, isInitialMount);
        onEntering?.(node, isInitialMount);
        controlsRef.current = animate(node, {
          opacity: 1,
          scale: 1
        }, ENTER_OPTIONS);
        controlsRef.current.then(() => onEntered?.(node, isInitialMount));
      }
    } else if (!isInitialMount) {
      onExit?.(node);
      onExiting?.(node);
      controlsRef.current = animate(node, {
        opacity: 0,
        scale: hiddenScale
      }, EXIT_OPTIONS);
      controlsRef.current.then(() => {
        onExited?.(node);
        setExited(true);
      });
    }
    return () => controlsRef.current?.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inProp]);
  if (!child) return null;
  const childProps = Object.fromEntries(Object.entries(rest).filter(([key]) => !TRANSITION_ONLY_PROPS.has(key)));
  return cloneElement(child, {
    ...childProps,
    ref: handleRef,
    style: {
      opacity: 0,
      transform: `scale(${hiddenScale})`,
      visibility: !inProp && exited ? 'hidden' : undefined,
      ...style,
      ...child.props.style
    }
  });
});